import { useEffect, useState, useMemo } from 'react';
import {
  Modal,
  TextInput,
  NumberInput,
  Select,
  Button,
  Stack,
  Group,
  Text,
  Divider,
  Checkbox,
  Collapse,
  Textarea,
  Box,
  ActionIcon,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { DateInput } from '@mantine/dates';
import { useForm, zodResolver } from '@mantine/form';
import { buildAssetSchema, type AssetFormValues } from '../schemas/assetSchema';
import { ImageUpload } from '@/components/ui/ImageUpload';
import { FormField } from '@/components/ui/FormField';
import { HierarchicalLocationPicker } from '@/components/ui/HierarchicalLocationPicker';
import { useAssetFieldSettings } from '@/hooks/api/useOrganizationSettings';
import { useCategories, useCreateCategory } from '@/hooks/api/useCategories';
import { useDepartments, useCreateDepartment } from '@/hooks/api/useDepartments';
import { useCustomFieldsForCategory } from '@/hooks/api/useCustomFields';
import { useOrganization } from '@/hooks/useOrganization';
import { CategoryForm } from '@/features/categories/components/CategoryForm';
import { DepartmentForm } from '@/features/departments/components/DepartmentForm';
import type { Asset } from '@/types/models';
import type { CategoryFormValues } from '@/features/categories/schemas/categorySchema';
import type { DepartmentFormValues } from '@/features/departments/schemas/departmentSchema';

interface AssetFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: AssetFormValues, photo?: File | null, keepOpen?: boolean) => void;
  asset?: Asset | null;
  isLoading?: boolean;
}

export function AssetForm({ opened, onClose, onSubmit, asset, isLoading }: AssetFormProps) {
  const { organizationId } = useOrganization();
  const fieldSettings = useAssetFieldSettings(organizationId || '');
  const { data: categories = [] } = useCategories(organizationId);
  const { data: departments = [] } = useDepartments(organizationId);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  // Quick-add modal states
  const [categoryModalOpened, setCategoryModalOpened] = useState(false);
  const [departmentModalOpened, setDepartmentModalOpened] = useState(false);

  // Mutation hooks for quick-add
  const createCategory = useCreateCategory();
  const createDepartment = useCreateDepartment();

  // Fetch custom fields for the selected category
  // Returns { universal: fields for all categories, categorySpecific: fields for this category }
  const { data: customFieldsData } = useCustomFieldsForCategory(organizationId, selectedCategoryId);
  const universalFields = customFieldsData?.universal || [];
  const categorySpecificFields = customFieldsData?.categorySpecific || [];
  const hasCustomFields = universalFields.length > 0 || categorySpecificFields.length > 0;

  // Get the selected category name for display
  const selectedCategoryName = useMemo(() => {
    if (!selectedCategoryId) return null;
    const cat = categories.find((c) => c.id === selectedCategoryId);
    return cat?.name || null;
  }, [selectedCategoryId, categories]);

  // Build dynamic schema based on organization settings
  const schema = useMemo(() => buildAssetSchema(fieldSettings), [fieldSettings]);

  // Prepare dropdown data
  const categoryOptions = useMemo(
    () => categories.map((cat) => ({ value: cat.id, label: cat.name })),
    [categories]
  );
  const departmentOptions = useMemo(
    () => departments.map((dept) => ({ value: dept.id, label: dept.name })),
    [departments]
  );

  const form = useForm<AssetFormValues>({
    validate: zodResolver(schema),
    initialValues: {
      description: '',
      tag_id: '',
      brand: '',
      model: '',
      serial_number: '',
      status: 'Available',
      cost: null,
      purchase_date: new Date().toISOString().split('T')[0],
      purchased_from: '',
      warranty_period: null,
      site_id: null,
      location_id: null,
      category_id: null,
      department_id: null,
      is_depreciable: false,
      depreciable_cost: null,
      asset_life_months: null,
      salvage_value: null,
      depreciation_method: null,
      date_acquired: null,
    },
  });

  // Update form when asset changes (for editing)
  useEffect(() => {
    if (asset) {
      form.setValues({
        description: asset.description || '',
        tag_id: asset.tag_id || '',
        brand: asset.brand || '',
        model: asset.model || '',
        serial_number: asset.serial_number || '',
        status: asset.status,
        cost: asset.cost,
        purchase_date: asset.purchase_date || '',
        purchased_from: asset.purchased_from || '',
        warranty_period: asset.warranty_period,
        site_id: asset.site_id,
        location_id: asset.location_id,
        category_id: asset.category_id,
        department_id: asset.department_id,
        is_depreciable: asset.is_depreciable || false,
        depreciable_cost: asset.depreciable_cost || null,
        asset_life_months: asset.asset_life_months || null,
        salvage_value: asset.salvage_value || null,
        depreciation_method: (asset.depreciation_method as any) || null,
        date_acquired: asset.date_acquired || null,
      });
      setSelectedCategoryId(asset.category_id);
      setPhotoPreview(asset.photo_url || null);
      setPhotoFile(null);
    } else {
      form.reset();
      setSelectedCategoryId(null);
      setPhotoPreview(null);
      setPhotoFile(null);
    }
  }, [asset]);

  const handlePhotoChange = (file: File | null, previewUrl?: string) => {
    setPhotoFile(file);
    if (previewUrl) {
      setPhotoPreview(previewUrl);
    }
  };

  const handlePhotoRemove = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handleSubmit = (values: AssetFormValues, keepOpen = false) => {
    onSubmit(values, photoFile, keepOpen);
    // Only reset form if keeping modal open (for "Create & Add Another")
    if (keepOpen) {
      form.reset();
      setPhotoFile(null);
      setPhotoPreview(null);
      setSelectedCategoryId(null);
    }
  };

  const handleClose = () => {
    form.reset();
    setPhotoFile(null);
    setPhotoPreview(null);
    onClose();
  };

  // Quick-add handlers
  const handleQuickAddCategory = async (values: CategoryFormValues) => {
    if (!organizationId) return;

    const newCategory = await createCategory.mutateAsync({
      ...values,
      organization_id: organizationId,
    });

    // Auto-fill the category field with the newly created category
    form.setFieldValue('category_id', newCategory.id);
    setSelectedCategoryId(newCategory.id);
    setCategoryModalOpened(false);
  };

  const handleQuickAddDepartment = async (values: DepartmentFormValues) => {
    if (!organizationId) return;

    const newDepartment = await createDepartment.mutateAsync({
      ...values,
      organization_id: organizationId,
    });

    // Auto-fill the department field with the newly created department
    form.setFieldValue('department_id', newDepartment.id);
    setDepartmentModalOpened(false);
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={asset ? 'Edit Asset' : 'Add New Asset'}
        size="lg"
        styles={{
          body: {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 120px)',
            overflow: 'hidden',
          },
        }}
      >
        <form
          onSubmit={form.onSubmit(handleSubmit)}
          onKeyDown={(e) => {
            // Allow Enter to submit form from any field (except textareas)
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
              e.preventDefault();
              form.onSubmit(handleSubmit)();
            }
          }}
          style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
        >
          <Box
            style={{
              flex: 1,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              padding: '1rem 1.5rem',
            }}
          >
            <Stack gap="md">
          {fieldSettings.tag_id?.visible !== false && (
            <FormField
              label="Asset Tag / ID"
              required={fieldSettings.tag_id?.required}
              error={form.errors.tag_id}
            >
              <TextInput
                placeholder="e.g., LAP-001"
                {...form.getInputProps('tag_id')}
                error={undefined}
              />
            </FormField>
          )}

          {fieldSettings.description?.visible !== false && (
            <FormField
              label="Description"
              required={fieldSettings.description?.required}
              error={form.errors.description}
            >
              <TextInput
                placeholder="e.g., MacBook Pro 16-inch"
                {...form.getInputProps('description')}
                error={undefined}
              />
            </FormField>
          )}

          {fieldSettings.brand?.visible !== false && (
            <FormField
              label="Brand"
              required={fieldSettings.brand?.required}
              error={form.errors.brand}
            >
              <TextInput
                placeholder="e.g., Apple"
                {...form.getInputProps('brand')}
                error={undefined}
              />
            </FormField>
          )}

          {fieldSettings.model?.visible !== false && (
            <FormField
              label="Model"
              required={fieldSettings.model?.required}
              error={form.errors.model}
            >
              <TextInput
                placeholder="e.g., MacBook Pro 2023"
                {...form.getInputProps('model')}
                error={undefined}
              />
            </FormField>
          )}

          {fieldSettings.serial_number?.visible !== false && (
            <FormField
              label="Serial Number"
              required={fieldSettings.serial_number?.required}
              error={form.errors.serial_number}
            >
              <TextInput
                placeholder="e.g., C02XY1ZGJHD"
                {...form.getInputProps('serial_number')}
                error={undefined}
              />
            </FormField>
          )}

          {fieldSettings.status?.visible !== false && (
            <FormField label="Status" required error={form.errors.status}>
              <Select
                data={[
                  { value: 'Available', label: 'Available' },
                  { value: 'Assigned', label: 'Assigned' },
                  { value: 'Maintenance', label: 'Maintenance' },
                  { value: 'Retired', label: 'Retired' },
                  { value: 'Lost', label: 'Lost' },
                ]}
                {...form.getInputProps('status')}
                error={undefined}
              />
            </FormField>
          )}

          {fieldSettings.cost?.visible !== false && (
            <FormField label="Cost" error={form.errors.cost}>
              <NumberInput
                placeholder="0.00"
                prefix="$"
                decimalScale={2}
                {...form.getInputProps('cost')}
                error={undefined}
              />
            </FormField>
          )}

          {fieldSettings.warranty_period?.visible !== false && (
            <FormField label="Warranty (months)" error={form.errors.warranty_period}>
              <NumberInput
                placeholder="12"
                min={0}
                {...form.getInputProps('warranty_period')}
                error={undefined}
              />
            </FormField>
          )}

          {fieldSettings.purchase_date?.visible !== false && (
            <FormField
              label="Purchase Date"
              required={fieldSettings.purchase_date?.required}
              error={form.errors.purchase_date}
            >
              <DateInput
                valueFormat="YYYY-MM-DD"
                value={form.values.purchase_date ? new Date(form.values.purchase_date) : null}
                onChange={(date) =>
                  form.setFieldValue('purchase_date', date?.toISOString().split('T')[0] || '')
                }
                error={undefined}
              />
            </FormField>
          )}

          {fieldSettings.purchased_from?.visible !== false && (
            <FormField
              label="Purchased From"
              required={fieldSettings.purchased_from?.required}
              error={form.errors.purchased_from}
            >
              <TextInput
                placeholder="e.g., Best Buy"
                {...form.getInputProps('purchased_from')}
                error={undefined}
              />
            </FormField>
          )}

          <Divider label="Associations" labelPosition="left" my="md" />

          {fieldSettings.category_id?.visible !== false && (
            <FormField label="Category" error={form.errors.category_id}>
              <Group gap="xs" wrap="nowrap">
                <Select
                  placeholder="Select category"
                  data={categoryOptions}
                  searchable
                  clearable
                  nothingFoundMessage="No categories found"
                  {...form.getInputProps('category_id')}
                  onChange={(value) => {
                    form.setFieldValue('category_id', value);
                    setSelectedCategoryId(value);
                  }}
                  error={undefined}
                  style={{ flex: 1 }}
                />
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={() => setCategoryModalOpened(true)}
                  title="Add new category"
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Group>
            </FormField>
          )}

          {fieldSettings.location_id?.visible !== false && (
            <FormField label="Location" error={form.errors.location_id || form.errors.site_id}>
              <HierarchicalLocationPicker
                siteId={form.values.site_id}
                locationId={form.values.location_id}
                onSiteChange={(siteId) => form.setFieldValue('site_id', siteId)}
                onLocationChange={(locationId) => form.setFieldValue('location_id', locationId)}
                disabled={isLoading}
              />
            </FormField>
          )}

          {fieldSettings.department_id?.visible !== false && (
            <FormField label="Department" error={form.errors.department_id}>
              <Group gap="xs" wrap="nowrap">
                <Select
                  placeholder="Select department"
                  data={departmentOptions}
                  searchable
                  clearable
                  nothingFoundMessage="No departments found"
                  {...form.getInputProps('department_id')}
                  error={undefined}
                  style={{ flex: 1 }}
                />
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={() => setDepartmentModalOpened(true)}
                  title="Add new department"
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Group>
            </FormField>
          )}

          {/* Custom Fields Section - Universal (applies to all categories) */}
          {universalFields.length > 0 && (
            <>
              <Divider label="Custom Fields" labelPosition="left" my="md" />
              {universalFields.map((field) => {
                switch (field.field_type) {
                  case 'text':
                    return (
                      <FormField key={field.id} label={field.name} required={field.is_required}>
                        <TextInput placeholder={field.default_value || ''} />
                      </FormField>
                    );
                  case 'textarea':
                    return (
                      <FormField key={field.id} label={field.name} required={field.is_required}>
                        <Textarea placeholder={field.default_value || ''} minRows={3} />
                      </FormField>
                    );
                  case 'number':
                    return (
                      <FormField key={field.id} label={field.name} required={field.is_required}>
                        <NumberInput placeholder={field.default_value || ''} />
                      </FormField>
                    );
                  case 'date':
                    return (
                      <FormField key={field.id} label={field.name} required={field.is_required}>
                        <DateInput valueFormat="YYYY-MM-DD" />
                      </FormField>
                    );
                  case 'dropdown':
                    return (
                      <FormField key={field.id} label={field.name} required={field.is_required}>
                        <Select
                          placeholder="Select option"
                          data={field.options || []}
                          searchable
                          clearable
                        />
                      </FormField>
                    );
                  case 'checkbox':
                    return (
                      <Box key={field.id} ml="35%" mt="xs">
                        <Checkbox label={field.name} />
                      </Box>
                    );
                  default:
                    return null;
                }
              })}
            </>
          )}

          {/* Custom Fields Section - Category-specific */}
          {categorySpecificFields.length > 0 && selectedCategoryName && (
            <>
              <Divider
                label={`Custom Fields for ${selectedCategoryName}`}
                labelPosition="left"
                my="md"
              />
              {categorySpecificFields.map((field) => {
                switch (field.field_type) {
                  case 'text':
                    return (
                      <FormField key={field.id} label={field.name} required={field.is_required}>
                        <TextInput placeholder={field.default_value || ''} />
                      </FormField>
                    );
                  case 'textarea':
                    return (
                      <FormField key={field.id} label={field.name} required={field.is_required}>
                        <Textarea placeholder={field.default_value || ''} minRows={3} />
                      </FormField>
                    );
                  case 'number':
                    return (
                      <FormField key={field.id} label={field.name} required={field.is_required}>
                        <NumberInput placeholder={field.default_value || ''} />
                      </FormField>
                    );
                  case 'date':
                    return (
                      <FormField key={field.id} label={field.name} required={field.is_required}>
                        <DateInput valueFormat="YYYY-MM-DD" />
                      </FormField>
                    );
                  case 'dropdown':
                    return (
                      <FormField key={field.id} label={field.name} required={field.is_required}>
                        <Select
                          placeholder="Select option"
                          data={field.options || []}
                          searchable
                          clearable
                        />
                      </FormField>
                    );
                  case 'checkbox':
                    return (
                      <Box key={field.id} ml="35%" mt="xs">
                        <Checkbox label={field.name} />
                      </Box>
                    );
                  default:
                    return null;
                }
              })}
            </>
          )}

          <Divider my="md" />

          {fieldSettings.photo_url?.visible !== false && (
            <FormField label="Asset Photo">
              <ImageUpload
                value={photoPreview}
                onChange={handlePhotoChange}
                onRemove={handlePhotoRemove}
                disabled={isLoading}
                loading={isLoading}
              />
            </FormField>
          )}

          <Divider my="md" />

          <Checkbox
            label="Track depreciation for this asset"
            description="Enable to calculate depreciation over time"
            {...form.getInputProps('is_depreciable', { type: 'checkbox' })}
          />

          <Collapse in={form.values.is_depreciable}>
            <Stack gap="md" mt="md">
              <FormField
                label="Depreciable Cost"
                error={form.errors.depreciable_cost}
              >
                <NumberInput
                  placeholder="0.00"
                  prefix="$"
                  decimalScale={2}
                  description="Cost basis for depreciation"
                  {...form.getInputProps('depreciable_cost')}
                  error={undefined}
                />
              </FormField>

              <FormField
                label="Salvage Value"
                error={form.errors.salvage_value}
              >
                <NumberInput
                  placeholder="0.00"
                  prefix="$"
                  decimalScale={2}
                  description="Expected value at end of life"
                  {...form.getInputProps('salvage_value')}
                  error={undefined}
                />
              </FormField>

              <FormField
                label="Asset Life (months)"
                error={form.errors.asset_life_months}
              >
                <NumberInput
                  placeholder="60"
                  min={1}
                  description="Useful life in months"
                  {...form.getInputProps('asset_life_months')}
                  error={undefined}
                />
              </FormField>

              <FormField
                label="Depreciation Method"
                error={form.errors.depreciation_method}
              >
                <Select
                  placeholder="Select method"
                  data={[
                    { value: 'straight-line', label: 'Straight-Line' },
                    { value: 'declining-balance', label: 'Declining Balance' },
                    { value: 'double-declining', label: 'Double Declining Balance' },
                    { value: 'sum-of-years', label: 'Sum of Years Digits' },
                  ]}
                  {...form.getInputProps('depreciation_method')}
                  error={undefined}
                />
              </FormField>

              <FormField
                label="Date Acquired"
                error={form.errors.date_acquired}
              >
                <DateInput
                  description="Date placed in service"
                  valueFormat="YYYY-MM-DD"
                  value={form.values.date_acquired ? new Date(form.values.date_acquired) : null}
                  onChange={(date) =>
                    form.setFieldValue('date_acquired', date?.toISOString().split('T')[0] || null)
                  }
                  error={undefined}
                />
              </FormField>
            </Stack>
          </Collapse>

            </Stack>
          </Box>

          {/* Sticky bottom buttons */}
          <Box
            style={{
              borderTop: '1px solid var(--mantine-color-gray-3)',
              backgroundColor: 'var(--mantine-color-body)',
              padding: '1rem 1.5rem',
              flexShrink: 0,
            }}
          >
            <Group justify="flex-end" wrap="wrap" gap="sm">
              <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
                Cancel
              </Button>
              {!asset && (
                <Button
                  type="button"
                  variant="light"
                  onClick={() => form.onSubmit((values) => handleSubmit(values, true))()}
                  loading={isLoading}
                >
                  Create & Add Another
                </Button>
              )}
              <Button
                type="button"
                onClick={() => form.onSubmit((values) => handleSubmit(values, false))()}
                loading={isLoading}
              >
                {asset ? 'Update Asset' : 'Create Asset'}
              </Button>
            </Group>
          </Box>
        </form>
      </Modal>

      {/* Quick-add modals */}
      <CategoryForm
        opened={categoryModalOpened}
        onClose={() => setCategoryModalOpened(false)}
        onSubmit={handleQuickAddCategory}
        isLoading={createCategory.isPending}
        inlineMode
      />

      <DepartmentForm
        opened={departmentModalOpened}
        onClose={() => setDepartmentModalOpened(false)}
        onSubmit={handleQuickAddDepartment}
        isLoading={createDepartment.isPending}
        inlineMode
      />
    </>
  );
}
